import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useConfirm } from "../../hooks/useConfirm";
import { useAuth } from "../../context/AuthContext";
import {
    Share2,
    Plus,
    Trash2,
    ChevronDown,
    Database,
    Loader2,
    Crown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@shadcn-in-nx/ui/button";
import { Alert, AlertDescription } from "@shadcn-in-nx/ui/alert";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@shadcn-in-nx/ui/command";
import { Label } from "@shadcn-in-nx/ui/label";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";

function DatabaseSharingPanel({ workflowId }) {
    const { t } = useTranslation();
    const { isFeatureEnabled, user } = useAuth();
    const companyId = user?.company?.id;
    // Allow shared databases for Innovator+ plans OR specific allowed companies
    const allowedCompanyIds = ["cmjoa3ng70jbyqp1ju559unxy"];
    const hasSharedDatabasesAccess = isFeatureEnabled("sharedDatabases") || allowedCompanyIds.includes(companyId);
    const [confirm, ConfirmDialog] = useConfirm();
    const [databases, setDatabases] = useState([]);
    const [grants, setGrants] = useState([]);
    const [outgoingGrants, setOutgoingGrants] = useState([]);
    const [incomingGrants, setIncomingGrants] = useState([]);
    const [companyWorkflows, setCompanyWorkflows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showGrantDialog, setShowGrantDialog] = useState(false);
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const [newGrant, setNewGrant] = useState({
        workflowId: "",
        accessType: "read",
        allowedTables: [],
    });
    const [workflowPopoverOpen, setWorkflowPopoverOpen] = useState(false);

    const { call: fetchDatabases } = useBluelagoon({ method: "getDatabases" });
    const { call: fetchGrants } = useBluelagoon({
        method: "getDatabaseAccessGrants",
    });
    const { call: fetchWorkflows } = useBluelagoon({
        method: "getCompanyWorkflows",
    });
    const { call: createGrant, isLoading: isCreating } = useBluelagoon({
        method: "createDatabaseAccessGrant",
    });
    const { call: deleteGrant, isLoading: isDeleting } = useBluelagoon({
        method: "deleteDatabaseAccessGrant",
    });

    const loadData = useCallback(async () => {
        if (!workflowId) return;
        setIsLoading(true);
        try {
            const [dbRes, grantsRes, workflowsRes] = await Promise.all([
                fetchDatabases({ workflowId }),
                fetchGrants({ workflowId }),
                fetchWorkflows({}),
            ]);

            // useBluelagoon returns the data directly, not wrapped in { data: ... }
            if (dbRes?.data?.databases) {
                setDatabases(dbRes.data.databases);
            }
            if (grantsRes?.data?.grants) {
                setGrants(grantsRes.data.grants);
            }
            if (grantsRes?.data?.outgoingGrants) {
                setOutgoingGrants(grantsRes.data.outgoingGrants);
            }
            if (grantsRes?.data?.incomingGrants) {
                setIncomingGrants(grantsRes.data.incomingGrants);
            }
            if (workflowsRes?.data?.workflows) {
                // Filter out current workflow
                setCompanyWorkflows(
                    workflowsRes.data.workflows.filter(
                        (w) => w.id !== workflowId
                    )
                );
            }
        } catch (error) {
            console.error("Error loading data:", error);
            toast.error(
                t("databaseSharing.loadError") || "Failed to load data"
            );
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, fetchDatabases, fetchGrants, fetchWorkflows, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleOpenGrantDialog = (database) => {
        setSelectedDatabase(database);
        setNewGrant({
            workflowId: "",
            accessType: "read",
            allowedTables: [],
        });
        setShowGrantDialog(true);
    };

    const handleCreateGrant = async () => {
        if (!selectedDatabase || !newGrant.workflowId) {
            toast.error(
                t("databaseSharing.selectWorkflow") || "Please select an app"
            );
            return;
        }

        try {
            await createGrant({
                databaseId: selectedDatabase.id,
                grantedWorkflowId: newGrant.workflowId,
                accessType: newGrant.accessType,
                allowedTables:
                    newGrant.allowedTables.length > 0
                        ? newGrant.allowedTables
                        : undefined,
            });

            toast.success(
                t("databaseSharing.grantCreated") ||
                    "Access granted successfully"
            );
            setShowGrantDialog(false);
            loadData();
        } catch (error) {
            console.error("Error creating grant:", error);
            toast.error(
                error?.message ||
                    t("databaseSharing.grantError") ||
                    "Failed to grant access"
            );
        }
    };

    const handleDeleteGrant = async (grantId) => {
        try {
            await confirm({
                title: t("databaseSharing.revokeAccessTitle") || "Revoke Access",
                description:
                    t("databaseSharing.revokeAccessDescription") ||
                    "Are you sure you want to revoke this access? The app will no longer be able to access this database.",
                confirmText: t("databaseSharing.revoke") || "Revoke",
                cancelText: t("common.cancel") || "Cancel",
                isDestructive: true,
            });
            await deleteGrant({ grantId });
            toast.success(
                t("databaseSharing.grantDeleted") ||
                    "Access revoked successfully"
            );
            loadData();
        } catch (error) {
            if (error === undefined) return;
            console.error("Error deleting grant:", error);
            toast.error(
                t("databaseSharing.deleteError") || "Failed to revoke access"
            );
        }
    };

    const toggleTableSelection = (tableName) => {
        setNewGrant((prev) => ({
            ...prev,
            allowedTables: prev.allowedTables.includes(tableName)
                ? prev.allowedTables.filter((t) => t !== tableName)
                : [...prev.allowedTables, tableName],
        }));
    };

    const getGrantsForDatabase = (dbId) => {
        return grants.filter((g) => g.databaseId === dbId);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    const ownedDatabases = databases.filter(
        (db) => db.isActiveWorkflowDatabase
    );

    return (
        <div className="p-6 space-y-6 w-full">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Share2 className="w-5 h-5" />
                    <h2 className="text-lg font-semibold">
                        {t("databaseSharing.title") || "Database Sharing"}
                    </h2>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                        BETA
                    </span>
                </div>
            </div>

            {/* Plan Restriction Alert */}
            {!hasSharedDatabasesAccess && (
                <Alert className="bg-purple-50 border-purple-200">
                    <Crown className="h-4 w-4 text-purple-600" />
                    <AlertDescription className="text-purple-700">
                        {t("databaseSharing.upgradeRequired") ||
                            "Database sharing is available on Innovator plan and above. Upgrade your plan to share databases across your apps."}
                    </AlertDescription>
                </Alert>
            )}

            {/* Incoming Grants - Databases this app has access to */}
            {incomingGrants.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">
                            {t("databaseSharing.accessibleDatabases") ||
                                "Databases You Have Access To"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {incomingGrants.map((grant) => (
                            <div key={grant.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div>
                                    <span className="font-medium text-sm">
                                        {grant.database?.name || "Unknown Database"}
                                    </span>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Badge
                                            variant={grant.accessType === "readwrite" ? "default" : "secondary"}
                                            className="text-xs"
                                        >
                                            {grant.accessType}
                                        </Badge>
                                        {grant.allowedTables?.length > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                                {t("databaseSharing.tables") || "Tables"}: {grant.allowedTables.join(", ")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteGrant(grant.id)}
                                    disabled={isDeleting}
                                >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Outgoing Grants - Databases this app owns and can share */}
            {ownedDatabases.length === 0 ? (
                <Alert>
                    <AlertDescription>
                        {t("databaseSharing.emptyDescription") ||
                            "This app does not have databases yet. Create entities in your app to generate databases that can be shared with other apps."}
                    </AlertDescription>
                </Alert>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">
                            {t("databaseSharing.shareDatabases") || "Share Your Databases"}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {ownedDatabases.map((database) => {
                            const dbGrants = getGrantsForDatabase(database.id);
                            return (
                                <div key={database.id} className="space-y-2">
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                        <div>
                                            <span className="font-medium text-sm">{database.name}</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge variant="secondary" className="text-xs">
                                                    {database.tables?.length || 0} {t("databaseSharing.tables") || "tables"}
                                                </Badge>
                                                <Badge variant="outline" className="text-xs">
                                                    {dbGrants.length} {t("databaseSharing.grants") || "grants"}
                                                </Badge>
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleOpenGrantDialog(database)}
                                            disabled={!hasSharedDatabasesAccess}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            {t("databaseSharing.share") || "Share"}
                                        </Button>
                                    </div>
                                    {dbGrants.length > 0 && dbGrants.map((grant) => (
                                        <div key={grant.id} className="flex items-center justify-between p-2 ml-4 bg-gray-50/50 rounded">
                                            <div>
                                                <span className="font-medium text-sm">
                                                    {grant.grantedWorkflow?.name || "Unknown App"}
                                                </span>
                                                <Badge variant={grant.accessType === "readwrite" ? "default" : "secondary"} className="ml-2 text-xs">
                                                    {grant.accessType}
                                                </Badge>
                                                {grant.allowedTables?.length > 0 && (
                                                    <span className="text-xs text-muted-foreground ml-2">
                                                        {grant.allowedTables.join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => handleDeleteGrant(grant.id)} disabled={isDeleting}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>
            )}

            {/* Grant Dialog */}
            <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
                <DialogContent className="max-w-lg overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>
                            {t("databaseSharing.shareDatabase") ||
                                "Share Database"}
                        </DialogTitle>
                        <DialogDescription>
                            {t("databaseSharing.shareDescription") ||
                                `Grant another app access to "${selectedDatabase?.name}"`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4 min-w-0">
                        <div className="space-y-2 min-w-0">
                            <Label>
                                {t("databaseSharing.selectApp") || "App"}
                            </Label>
                            <Popover
                                open={workflowPopoverOpen}
                                onOpenChange={setWorkflowPopoverOpen}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={workflowPopoverOpen}
                                        className="w-full min-w-0 justify-between font-normal overflow-hidden"
                                    >
                                        <span className="truncate">
                                            {newGrant.workflowId
                                                ? companyWorkflows.find(
                                                      (wf) =>
                                                          wf.id ===
                                                          newGrant.workflowId
                                                  )?.name
                                                : t(
                                                      "databaseSharing.selectAppPlaceholder"
                                                  ) || "Select an app..."}
                                        </span>
                                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                    <Command>
                                        <CommandInput
                                            placeholder={
                                                t(
                                                    "databaseSharing.searchApps"
                                                ) || "Search apps..."
                                            }
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                {t(
                                                    "databaseSharing.noAppsFound"
                                                ) || "No apps found."}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {companyWorkflows.map((wf) => (
                                                    <CommandItem
                                                        key={wf.id}
                                                        value={wf.name}
                                                        onSelect={() => {
                                                            setNewGrant(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    workflowId:
                                                                        wf.id,
                                                                })
                                                            );
                                                            setWorkflowPopoverOpen(
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        {wf.name}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-2">
                            <Label>
                                {t("databaseSharing.accessLevel") ||
                                    "Access Level"}
                            </Label>
                            <Select
                                value={newGrant.accessType}
                                onValueChange={(value) =>
                                    setNewGrant((prev) => ({
                                        ...prev,
                                        accessType: value,
                                    }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="read">
                                        {t("databaseSharing.readOnly") ||
                                            "Read Only"}
                                    </SelectItem>
                                    <SelectItem value="write">
                                        {t("databaseSharing.writeOnly") ||
                                            "Write Only"}
                                    </SelectItem>
                                    <SelectItem value="readwrite">
                                        {t("databaseSharing.readWrite") ||
                                            "Read & Write"}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {selectedDatabase?.tables?.length > 0 && (
                            <div className="space-y-2">
                                <Label>
                                    {t("databaseSharing.restrictTables") ||
                                        "Restrict to specific tables (optional)"}
                                </Label>
                                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                                    {selectedDatabase.tables.map((table) => (
                                        <div
                                            key={table.id}
                                            className="flex items-center space-x-2"
                                        >
                                            <Checkbox
                                                id={table.id}
                                                checked={newGrant.allowedTables.includes(
                                                    table.name
                                                )}
                                                onCheckedChange={() =>
                                                    toggleTableSelection(
                                                        table.name
                                                    )
                                                }
                                            />
                                            <label
                                                htmlFor={table.id}
                                                className="text-sm"
                                            >
                                                {table.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500">
                                    {t("databaseSharing.noSelectionMeansAll") ||
                                        "Leave empty to allow access to all tables"}
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowGrantDialog(false)}
                        >
                            {t("common.cancel") || "Cancel"}
                        </Button>
                        <Button
                            onClick={handleCreateGrant}
                            disabled={isCreating}
                        >
                            {isCreating
                                ? t("common.loading") || "..."
                                : t("databaseSharing.grantAccess") ||
                                  "Grant Access"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog />
        </div>
    );
}

export default DatabaseSharingPanel;
